import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LangProvider } from "@/i18n/LangContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { LangSync } from "@/components/LangSync";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PublicLayout } from "@/layouts/PublicLayout";
import { AppLayout } from "@/layouts/AppLayout";
import { roleHome } from "@/lib/roleHome";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AcceptInvitation from "@/pages/AcceptInvitation";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Programs from "@/pages/Programs";
import PaymentQueue from "@/pages/PaymentQueue";
import PaymentSettings from "@/pages/PaymentSettings";
import FoodJournal from "@/pages/FoodJournal";
import Branding from "@/pages/Branding";
import CoachCandidates from "@/pages/CoachCandidates";
import AdminUsers from "@/pages/AdminUsers";
import MyJourney from "@/pages/MyJourney";
import Measurements from "@/pages/Measurements";
import RecognitionWall from "@/pages/RecognitionWall";
import JournalModeration from "@/pages/JournalModeration";
import Education from "@/pages/Education";
import { Toaster } from "sonner";

function RoleRedirect() {
  const { user, checked } = useAuth();
  if (!checked) return null;
  return user ? <Navigate to={roleHome(user)} replace /> : <Navigate to="/login" replace />;
}

function Home() {
  const { user, checked } = useAuth();
  if (!checked) return null;
  return user ? <Navigate to={roleHome(user)} replace /> : <Landing />;
}

function App() {
  return (
    <div className="App">
      <LangProvider>
        <BrowserRouter>
          <AuthProvider>
            <BrandingProvider>
            <LangSync />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route element={<PublicLayout />}>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/accept-invitation" element={<AcceptInvitation />} />
              </Route>
              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/dashboard" element={<Dashboard scope="member" />} />
                <Route path="/coach" element={<ProtectedRoute roles={["COACH"]}><Dashboard scope="coach" /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute roles={["CO_ADMIN", "MASTER_ADMIN"]}><Dashboard scope="admin" /></ProtectedRoute>} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/programs" element={<Programs />} />
                <Route path="/my-journey" element={<MyJourney />} />
                <Route path="/coach-qualification" element={<Navigate to="/education" replace />} />
                <Route path="/admin/users" element={<ProtectedRoute roles={["MASTER_ADMIN"]}><AdminUsers /></ProtectedRoute>} />
                <Route path="/education" element={<ProtectedRoute permission="education.view"><Education /></ProtectedRoute>} />
                <Route path="/admin/payments" element={<ProtectedRoute permission="payment.view"><PaymentQueue /></ProtectedRoute>} />
                <Route path="/admin/payment-settings" element={<ProtectedRoute permission="payment_settings.view"><PaymentSettings /></ProtectedRoute>} />
                <Route path="/food-journal" element={<ProtectedRoute permission="food_journal.view"><FoodJournal /></ProtectedRoute>} />
                <Route path="/measurements" element={<ProtectedRoute permission="measurement.view"><Measurements /></ProtectedRoute>} />
                <Route path="/recognition" element={<ProtectedRoute permission="food_journal.view"><RecognitionWall /></ProtectedRoute>} />
                <Route path="/admin/journal" element={<ProtectedRoute permission="food_journal.moderate"><JournalModeration /></ProtectedRoute>} />
                <Route path="/admin/branding" element={<ProtectedRoute permission="branding.edit"><Branding /></ProtectedRoute>} />
                <Route path="/admin/coach-candidates" element={<ProtectedRoute permission="coach_candidate.view"><CoachCandidates /></ProtectedRoute>} />
              </Route>
              <Route path="*" element={<RoleRedirect />} />
            </Routes>
            <Toaster theme="dark" position="top-right" />
            </BrandingProvider>
          </AuthProvider>
        </BrowserRouter>
      </LangProvider>
    </div>
  );
}

export default App;
